import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import { fetchUserByGuid } from "../../features/users/data/userService";

const SignalRContext = createContext(null);

export const SignalRProvider = ({ userGuid, children }) => {
    const [lastEvent, setLastEvent] = useState(null);
    const [connectionId, setConnectionId] = useState(null);

    const socket = useRef(null);
    const isConnectedRef = useRef(false);
    const clientIpRef = useRef(null);
    const openedEntitiesRef = useRef(new Set());

    const app = "iQuoteNG";
    const host = process.env.REACT_APP_SIGNALR_URI.replace(/\/$/, "");
    const hub = "MaintelEntityLockHub";
    const HEALTH_CHECK_URL = `${host}/healthcheck`;
    const HEALTH_CHECK_INTERVAL_SECS = 5;
    const locksByEntityKey =  "locksByEntity";
    // event names raised by the hub
    const entityLockedEvent = "EntityLocked";
    const entityUnlockedEvent = "EntityUnlocked";
    const lockOverriddenEvent = "LockOverridden";
    // hub methods to call
    const openEntityMethod = "OpenEntity";
    const closeEntityMethod = "CloseEntity";
    const overrideLockMethod = "OverrideLock";
    const getEntityLockStateMethod = "GetEntityLockState";
    // log config    
    const enableConnectionLogs = true, enableEntityLogs = true;

    // Check server health in a configured interval
    const pingServer = () => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = `${HEALTH_CHECK_URL}?_=${Date.now()}`;
        });
    };

    useEffect(() => {
        if (!userGuid || process.env.REACT_APP_ENABLE_SIGNALR === "false") {
            if (socket.current) {
                socket.current.stop();
                socket.current = null;
            }
            isConnectedRef.current = false;
            clientIpRef.current = null;
            openedEntitiesRef.current.clear();
            return;
        }

        let healthCheckInterval;

        const connect = async () => {
            if (socket.current) return;

            const user = await fetchUserByGuid(userGuid);
            const username = user ? `${user.firstName} ${user.lastName}` : "";

            socket.current = new signalR.HubConnectionBuilder()
                .withUrl(`${host}/${hub}?app=${app}&username=${username}&userGuid=${userGuid}`)
                .build();

            socket.current.onreconnecting(() => {
                enableConnectionLogs && console.log("🔄 SignalR reconnecting...");
                isConnectedRef.current = false;
                clientIpRef.current = null;
            });

            socket.current.onreconnected(() => {
                enableConnectionLogs && console.log("✅ SignalR reconnected!");
                isConnectedRef.current = true;
            });

            socket.current.onclose(() => {
                enableConnectionLogs && console.log("❌ SignalR connection closed");
                isConnectedRef.current = false;
                clientIpRef.current = null;
                setConnectionId(null);
                socket.current = null;
            });

            socket.current.on(entityLockedEvent, (data) => {
                enableEntityLogs && console.log(`🔒 Entity locked: ${JSON.stringify(data)}`);
                const key = `${app}:${locksByEntityKey}:${data.entity}:${data.entityId}`;
                openedEntitiesRef.current.add(key);
                setLastEvent({ type: entityLockedEvent, data });
            });

            socket.current.on(entityUnlockedEvent, (data) => {
                enableEntityLogs && console.log(`🔓 Entity unlocked: ${JSON.stringify(data)}`);
                const key = `${app}:${locksByEntityKey}:${data.entity}:${data.entityId}`;
                openedEntitiesRef.current.delete(key);
                setLastEvent({ type: entityUnlockedEvent, data });
            });

            socket.current.on(lockOverriddenEvent, (data) => {
                enableEntityLogs && console.log(`🔄 Lock overridden: ${JSON.stringify(data)}`);
                setLastEvent({ type: lockOverriddenEvent, data });
            });

            socket.current.on("GetClientIp", (ip) => {
                clientIpRef.current = ip;
            });

            try {
                await socket.current.start();
                enableConnectionLogs && console.log("✅ SignalR connected!");
                enableConnectionLogs && console.log(`🌍 SignalR host: ${host}`);
                isConnectedRef.current = true;
                setConnectionId(socket.current.connectionId);
            } catch (err) {
                enableConnectionLogs && console.error("❌ SignalR connection failed:", err);
            }
        };

        const checkHealthAndConnect = async () => {
            const isAlive = await pingServer();
            if (isAlive) {
                if (!isConnectedRef.current) {
                    await connect();
                }
            } else {
                enableConnectionLogs && console.log(`❌ [${new Date().toLocaleString()}] SignalR Server ${host} not reachable, retrying in ${HEALTH_CHECK_INTERVAL_SECS} seconds...`);
                if (socket.current) {
                    await socket.current.stop().catch(() => {});
                    socket.current = null;
                }
                isConnectedRef.current = false;
                clientIpRef.current = null;
                setConnectionId(null);
            }
        };

        checkHealthAndConnect();

        healthCheckInterval = setInterval(checkHealthAndConnect, HEALTH_CHECK_INTERVAL_SECS * 1000);

        return () => {
            clearInterval(healthCheckInterval);
            if (socket.current) {
                socket.current.stop();
                socket.current = null;
            }
            openedEntitiesRef.current.clear();
        };
    }, [userGuid]);

    const openEntity = useCallback(async (entity, entityId, force = false) => {
        if (!isConnectedRef.current || !socket.current) return;

        const key = `${app}:${locksByEntityKey}:${entity}:${entityId}`;
        if (!force && openedEntitiesRef.current.has(key)) return;

        try {
            await socket.current.invoke(openEntityMethod, key);
            openedEntitiesRef.current.add(key);
        } catch (err) {
            enableEntityLogs && console.error("Failed to open entity", err);
        }
    }, [isConnectedRef.current]);

    const closeEntity = useCallback(async (entity, entityId) => {
        if (!isConnectedRef.current || !socket.current) return;

        const key = `${app}:${locksByEntityKey}:${entity}:${entityId}`;
        if (!openedEntitiesRef.current.has(key)) return;

        try {
            await socket.current.invoke(closeEntityMethod, key);
            openedEntitiesRef.current.delete(key);
            setLastEvent({ type: entityUnlockedEvent, data: { entity, entityId: entityId } });
        } catch (err) {
            enableEntityLogs && console.error("Failed to close entity", err);
        }
    }, [isConnectedRef.current]);

    const overrideLock = useCallback(async (entity, entityId) => {
        if (!isConnectedRef.current || !socket.current) return;

        const key = `${app}:${locksByEntityKey}:${entity}:${entityId}`;
        
        try {
            await socket.current.invoke(overrideLockMethod, key);
        } catch (err) {
            enableEntityLogs && console.error("Failed to override lock", err);
        }
    }, [isConnectedRef.current]);

    const getEntityLockState = useCallback(async (entity, entityId) => {
        if (!isConnectedRef.current || !socket.current) return null;

        try {
            const key = `${app}:${locksByEntityKey}:${entity}:${entityId}`;
            return await socket.current.invoke(getEntityLockStateMethod, key);
        } catch (err) {
            enableEntityLogs && console.error("Failed to get lock state:", err);
            return null;
        }
   }, [isConnectedRef.current]);

    return (
        <SignalRContext.Provider
            value={{
                lastEvent,
                isConnected: isConnectedRef.current,
                clientIp: clientIpRef.current,
                openedEntitiesRef,
                openEntity,
                closeEntity,
                overrideLock,
                userGuid,
                connectionId,
                getEntityLockState
            }}
        >
            {children}
        </SignalRContext.Provider>
    );
};
export const useSignalR = () => useContext(SignalRContext);